import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  KanbanSquare, List, Plus, X, Trash2, ChevronRight, Download,
  Calendar, User, Building2, GripVertical, Paperclip,
} from 'lucide-react';
import EmployeePicker from '../../components/EmployeePicker';
import Pagination from '../../components/Pagination';
import BulkActionBar from '../../components/BulkActionBar';
import SegmentedFilterBar from '../../components/SegmentedFilterBar';
import ScopeToggle from '../../components/ScopeToggle';
import CompanyPicker from '../../components/crm/CompanyPicker';
import ContactPicker from '../../components/crm/ContactPicker';
import MultiContactPicker from '../../components/crm/MultiContactPicker';
import ContactFormDrawer from '../../components/crm/ContactFormDrawer';
import ConsultantPanel from '../../components/crm/ConsultantPanel';
import { useScope } from '../../lib/hooks/useScope';
import { usePaginated } from '../../lib/hooks/usePaginated';
import { useRowSelection } from '../../lib/hooks/useRowSelection';
import { useSortable, SortIcon } from '../../lib/hooks/useSortable';
import { apiGet, apiPost, apiPut, apiPatch, apiDel } from '../../lib/api';
import { inrCompact, formatDate } from '../../lib/format';
import { toast } from 'sonner';
import { ProjectCombobox, LocationCombobox } from '../../components/pickers/ProjectClientPickers';
import LeadDetailDrawer from '../../components/crm/LeadDetailDrawer';
import StaleBadge from '../../components/crm/StaleBadge';
import DocumentUploader, { ENQUIRY_DOC_CATEGORIES } from '../../components/DocumentUploader';

// ── Constants ──────────────────────────────────────────────────────────────
const CR = 10000000;
const rsToCr = (v) => (v === '' || v === null || v === undefined) ? '' : (Number(v) / CR).toString();
const crToRs = (v) => { const n = parseFloat(v); if (Number.isNaN(n)) return null; return Math.round(n * CR); };

const STAGE_COLORS = {
  'Enquiry Received': 'bg-slate-100 border-slate-300 text-slate-700',
  'Qualification':    'bg-amber-100 border-amber-300 text-amber-800',
  'Quotation Sent':   'bg-teal-100 border-teal-300 text-teal-800',
  'Negotiation':       'bg-yellow-100 border-yellow-300 text-yellow-800',
  'PO Received':       'bg-emerald-100 border-emerald-300 text-emerald-800',
  'Lost':              'bg-red-100 border-red-300 text-red-700',
};

const DEFAULT_STAGES = [
  'Enquiry Received', 'Qualification', 'Quotation Sent',
  'Negotiation', 'Lost', 'PO Received',
];

const initialForm = {
  client: '', project_name: '', location: '', client_email: '',
  estimated_value: '', total_project_cost: '',
  stage: '', probability: 0, expected_close_date: '',
  owner: '', notes: '', lead_id: '',
  company_id: '', primary_contact_id: '', additional_contact_ids: [], consultants: [],
  source_links: ['', '', ''],
  tags: [], custom_fields: {},
};

// ── Component ──────────────────────────────────────────────────────────────
export default function ProjectsPipeline() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── View (kanban | list) ──────────────────────────────────────────────────
  const [view, setView] = useState(
    searchParams.get('view') === 'list' ? 'list' : 'kanban'
  );
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    setSearchParams(next, { replace: true });
  }, [view]); // eslint-disable-line

  // ── Pipeline stages (from CRM config) ────────────────────────────────────
  const [stages, setStages] = useState(DEFAULT_STAGES);
  useEffect(() => {
    apiGet('/crm/sla/config')
      .then((cfg) => {
        const list = cfg?.opportunity_stages;
        if (Array.isArray(list) && list.length) setStages(list);
      })
      .catch(() => {});
  }, []);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [opportunities, setOpportunities] = useState([]);
  const [kanbanData, setKanbanData] = useState({ stages: [], by_stage: {} });
  const { scope, setScope } = useScope('opportunities');
  const [filteredOpps, setFilteredOpps] = useState([]);

  const load = async () => {
    try {
      const [listRes, kanbanRes] = await Promise.all([
        apiGet(`/crm/opportunities?scope=${scope}`),
        apiGet('/crm/pipeline'),
      ]);
      setOpportunities(listRes.opportunities || []);
      const byStageFlat = {};
      for (const [stage, val] of Object.entries(kanbanRes.by_stage || {})) {
        byStageFlat[stage] = val.deals || [];
      }
      setKanbanData({ stages: kanbanRes.stages || [], by_stage: byStageFlat });
    } catch {
      toast.error('Could not load pipeline');
    }
  };

  useEffect(() => { if (scope) load(); }, [scope]); // eslint-disable-line

  // Reload on window focus / visibility (same pattern as OpportunityTracker)
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [scope]); // eslint-disable-line

  // ── Form state ─────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [formDocs, setFormDocs] = useState([]);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [drawerTarget, setDrawerTarget] = useState('primary');
  const [contactRefresh, setContactRefresh] = useState(0);
  const savedOppIdRef = useRef(null);
  const draftSavePromiseRef = useRef(null);

  // Handle ?add=1 and prefill from location.state
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setFormData({ ...initialForm, ...prefill });
      setShowForm(true);
    } else if (searchParams.get('add') === '1') {
      setShowForm(true);
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
  }, [location.state]); // eslint-disable-line

  // Handle ?edit=<id>
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || !opportunities.length) return;
    const opp = opportunities.find((o) => o.id === editId);
    if (opp) {
      openEditForm(opp);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, opportunities]); // eslint-disable-line

  const openEditForm = (opp) => {
    setEditingOpp(opp);
    savedOppIdRef.current = opp.id;
    const links = Array.isArray(opp.source_links) ? opp.source_links.slice(0, 3) : [];
    while (links.length < 3) links.push('');
    setFormData({
      ...initialForm,
      ...opp,
      estimated_value: opp.estimated_value ? rsToCr(opp.estimated_value) : '',
      total_project_cost: opp.total_project_cost ? rsToCr(opp.total_project_cost) : '',
      source_links: links,
    });
    apiGet(`/documents/opportunity/${opp.id}`)
      .then((res) => setFormDocs(res.documents || []))
      .catch(() => setFormDocs([]));
    setOpenOppId(null);
    setShowForm(true);
  };

  // Draft-save pattern: silently persist opp before first doc upload
  const ensureOppSaved = async () => {
    if (savedOppIdRef.current) return savedOppIdRef.current;
    if (draftSavePromiseRef.current) return draftSavePromiseRef.current;
    draftSavePromiseRef.current = (async () => {
      try {
        if (!formData.company_id || !formData.client || !formData.project_name || !formData.stage || !formData.owner) {
          toast.error('Fill Company, Project Name, Client, Stage and Owner before uploading documents.');
          return null;
        }
        const cleanedLinks = (formData.source_links || []).map((l) => (l || '').trim()).filter(Boolean);
        const payload = {
          ...formData,
          client_email: (formData.client_email || '').trim() || null,
          estimated_value: formData.estimated_value === '' ? 0 : (crToRs(formData.estimated_value) || 0),
          total_project_cost: formData.total_project_cost === '' ? null : crToRs(formData.total_project_cost),
          source_links: cleanedLinks,
          probability: 0,
        };
        const res = await apiPost('/crm/opportunities', payload);
        const id = res.id;
        savedOppIdRef.current = id;
        setEditingOpp({ ...formData, id });
        toast.success('Project draft saved — you can now upload documents');
        return id;
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not save draft');
        return null;
      } finally {
        draftSavePromiseRef.current = null;
      }
    })();
    return draftSavePromiseRef.current;
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingOpp(null);
    setFormData(initialForm);
    setFormDocs([]);
    savedOppIdRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanedLinks = (formData.source_links || []).map((l) => (l || '').trim()).filter(Boolean);
      if (cleanedLinks.length > 3) { toast.error('Maximum 3 source links allowed'); return; }
      if (formData.primary_contact_id && formData.additional_contact_ids?.includes(formData.primary_contact_id)) {
        toast.error('Primary contact cannot also be an additional contact'); return;
      }
      const payload = {
        ...formData,
        client_email: (formData.client_email || '').trim() || null,
        estimated_value: formData.estimated_value === '' ? 0 : (crToRs(formData.estimated_value) || 0),
        total_project_cost: formData.total_project_cost === '' ? null : crToRs(formData.total_project_cost),
        source_links: cleanedLinks,
        probability: 0,
      };
      if (editingOpp || savedOppIdRef.current) {
        const targetId = editingOpp?.id || savedOppIdRef.current;
        await apiPut(`/crm/opportunities/${targetId}`, payload);
        toast.success('Project updated');
      } else {
        await apiPost('/crm/opportunities', payload);
        toast.success('Project created');
      }
      resetForm();
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg ? `${detail[0].loc?.[1] || ''} ${detail[0].msg}` : 'Validation failed');
      } else {
        toast.error(detail || 'Failed to save project');
      }
    }
  };

  // ── Detail drawer ─────────────────────────────────────────────────────────
  const [openOppId, setOpenOppId] = useState(null);

  // ── Kanban drag-drop ──────────────────────────────────────────────────────
  const [dragOpp, setDragOpp] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const handleDragStart = (opp, e) => {
    setDragOpp(opp);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (stage) => {
    if (!dragOpp || dragOpp.stage === stage) { setDragOpp(null); setDragOverStage(null); return; }
    try {
      await apiPatch(`/crm/opportunities/${dragOpp.id}/stage`, { stage });
      load();
    } catch {
      toast.error('Stage update failed');
    }
    setDragOpp(null);
    setDragOverStage(null);
  };


  // ── Delete (works for both Kanban and List rows) ───────────────────────────
  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Remove this project?\n\nThis will soft-delete it — a super-admin can restore it.')) return;
    try {
      await apiDel(`/crm/opportunities/${id}`);
      toast.success('Project removed');
      load();
    } catch {
      toast.error('Could not delete project');
    }
  };

  // ── List: sorting / pagination / filters / bulk ops ───────────────────────
  const filterDefs = useMemo(() => {
    const owners = Array.from(new Set(opportunities.map((o) => o.owner).filter(Boolean))).sort();
    const clients = Array.from(new Set(opportunities.map((o) => o.client).filter(Boolean))).sort();
    return [
      { key: 'stage',  label: 'Stage',  type: 'multiSelect', options: stages },
      { key: 'owner',  label: 'Owner',  type: 'multiSelect', options: owners },
      { key: 'client', label: 'Client', type: 'multiSelect', options: clients },
    ];
  }, [opportunities, stages]);

  const { sorted, sortKey, sortDir, getProps } = useSortable(filteredOpps, { defaultKey: 'created_at', defaultDir: 'desc' });
  const pagination = usePaginated(sorted, { defaultPageSize: 10 });
  const pageIds = pagination.paged.map((o) => o.id);
  const selection = useRowSelection();

  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selected);
    if (!ids.length || !window.confirm(`Delete ${ids.length} selected project${ids.length === 1 ? '' : 's'}?\n\nThis cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => apiDel(`/crm/opportunities/${id}`)));
      toast.success(`Deleted ${ids.length} project${ids.length === 1 ? '' : 's'}`);
      selection.clear();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Bulk delete failed'); }
  };

  const handleBulkExport = () => {
    const rows = opportunities.filter((o) => selection.selected.has(o.id));
    const csv = [
      ['Project Name', 'Client', 'Stage', 'Estimated Order Value (Cr)', 'Client Project Value (Cr)', 'Expected Close', 'Owner'].join(','),
      ...rows.map((o) => [
        o.project_name, o.client, o.stage,
        o.estimated_value ? (o.estimated_value / CR).toFixed(2) : '',
        o.total_project_cost ? (o.total_project_cost / CR).toFixed(2) : '',
        o.expected_close_date, o.owner,
      ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `projects-pipeline-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} project${rows.length === 1 ? '' : 's'}`);
  };

  const stageColor = (s) => {
    if (s === 'PO Received') return 'bg-teal-100 text-teal-700';
    if (s === 'Lost') return 'bg-red-100 text-red-700';
    if (s === 'Quotation Sent') return 'bg-teal-100 text-teal-700';
    if (s === 'Qualification') return 'bg-amber-100 text-amber-700';
    if (s === 'Negotiation') return 'bg-amber-100 text-amber-700';
    return 'bg-teal-100 text-teal-700';
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div data-testid="projects-pipeline-page">

      {/* ── Header bar ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">Business Development</div>
          <h1 className="font-display text-xl font-extrabold tracking-tight">Projects Pipeline</h1>
          <p className="text-xs text-slate-500 mt-0.5">Inbound supply enquiries from builders and contractors — pre-order stage</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
            <button
              onClick={() => setView('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                view === 'kanban' ? 'bg-white text-[#1F7A6E] shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="pp-view-kanban"
            >
              <KanbanSquare size={13} /> Kanban
            </button>
            <button
              onClick={() => setView('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                view === 'list' ? 'bg-white text-[#1F7A6E] shadow-sm' : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="pp-view-list"
            >
              <List size={13} /> List
            </button>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="pp-add-project-btn"
          >
            <Plus size={15} /> Add Project
          </button>
        </div>
      </div>

      {/* ── Kanban view ── */}
      {view === 'kanban' && (
        <div className="p-4 flex flex-col gap-4">
          <div className="flex gap-3 overflow-x-auto pb-4 flex-1 items-start">
            {kanbanData.stages.map((stage) => {
              const cards = kanbanData.by_stage?.[stage] || [];
              const isOver = dragOverStage === stage;
              return (
                <div
                  key={stage}
                  className={`flex-shrink-0 w-64 rounded-sm border ${isOver ? 'border-[#1F7A6E] bg-teal-50/40' : 'border-slate-200 bg-slate-50'} flex flex-col`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage); }}
                  onDragLeave={() => setDragOverStage(null)}
                  onDrop={() => handleDrop(stage)}
                  data-testid={`kanban-col-${stage.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-600">{stage}</span>
                    <span className="text-[10px] bg-white border border-slate-200 rounded-full px-1.5 py-0.5 font-mono text-slate-500">{cards.length}</span>
                  </div>
                  <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[calc(100vh-220px)]">
                    {cards.map((d) => (
                      <div
                        key={d.id}
                        draggable
                        onDragStart={(e) => handleDragStart(d, e)}
                        onClick={() => setOpenOppId(d.id)}
                        className="border border-slate-200 rounded-sm bg-white p-3 hover:border-[#1F7A6E] cursor-pointer transition-colors group relative"
                        data-testid={`kanban-card-${d.id}`}
                      >
                        <button
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600 p-1 rounded z-10"
                          title="Delete project"
                          data-testid={`kanban-delete-${d.id}`}
                          onClick={(e) => handleDelete(d.id, e)}
                        >
                          <Trash2 size={13} />
                        </button>
                        <div className="absolute top-2.5 left-2 opacity-0 group-hover:opacity-40 transition-opacity">
                          <GripVertical size={13} className="text-slate-400" />
                        </div>
                        <div className="pl-3 pr-5">
                          <p className="text-xs font-display font-bold text-[#1F7A6E] leading-snug line-clamp-2 mb-1">
                            {d.project_name || d.name || '—'}
                          </p>
                          {d.client && (
                            <div className="flex items-center gap-1 text-[10px] text-slate-500">
                              <Building2 size={9} />{d.client}
                            </div>
                          )}
                          {d.estimated_value && (
                            <div className="text-[10px] text-slate-500 font-mono">{inrCompact(d.estimated_value)}</div>
                          )}
                          {d.owner && (
                            <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
                              <User size={9} />{d.owner}
                            </div>
                          )}
                          {d.expected_close_date && (
                            <div className="flex items-center gap-1 text-[10px] text-slate-400">
                              <Calendar size={9} />{new Date(d.expected_close_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                            </div>
                          )}
                          <StaleBadge updatedAt={d.updated_at} createdAt={d.created_at} className="mt-1" />
                        </div>
                        {stage === 'Qualification' && (d.documents_count || 0) === 0 && (
                          <div className="mt-2 pt-2 border-t border-slate-100">
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenOppId(d.id); }}
                              className="w-full text-[10px] uppercase tracking-wider font-display font-bold px-2 py-1.5 rounded-sm border border-dashed border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 flex items-center justify-center gap-1"
                              data-testid={`upload-docs-${d.id}`}
                            >
                              <Paperclip size={10} /> Upload Enquiry Docs
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {cards.length === 0 && (
                      <div className="text-center py-6 text-[10px] text-slate-400 border border-dashed border-slate-200 rounded-sm">
                        Drop here
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── List view ── */}
      {view === 'list' && (
        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <ScopeToggle value={scope} onChange={setScope} testid="scope-toggle-opportunities" />
            <div className="text-xs text-slate-500">
              Showing <b className="text-[#1F7A6E]">{opportunities.length}</b> projects ·{' '}
              <span className="uppercase tracking-wider font-display font-bold text-[10px]">{scope}</span>
            </div>
          </div>
          <SegmentedFilterBar
            entityType="opportunities"
            items={opportunities}
            filterDefs={filterDefs}
            onFiltered={setFilteredOpps}
          />
          <div className="mb-3" />
          <BulkActionBar count={selection.count} onClear={selection.clear} className="mb-3">
            <button onClick={handleBulkExport} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-white/15 text-white rounded-sm hover:bg-white/25 flex items-center gap-1" data-testid="bulk-export-opps">
              <Download size={11} /> Export CSV
            </button>
            <button onClick={handleBulkDelete} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-red-600 text-white rounded-sm hover:bg-red-700 flex items-center gap-1" data-testid="bulk-delete-opps">
              <Trash2 size={11} /> Delete
            </button>
          </BulkActionBar>
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full dense-table">
              <thead>
                <tr>
                  <th className="w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-[#1F7A6E] cursor-pointer"
                      checked={selection.allSelected(pageIds)}
                      ref={(el) => { if (el) el.indeterminate = selection.someSelected(pageIds); }}
                      onChange={() => selection.toggleAll(pageIds)}
                      data-testid="select-all-opps"
                    />
                  </th>
                  <th {...getProps('project_name')}>Project Name <SortIcon dir={sortKey === 'project_name' ? sortDir : null} /></th>
                  <th {...getProps('client')}>Client <SortIcon dir={sortKey === 'client' ? sortDir : null} /></th>
                  <th {...getProps('estimated_value')}>Order Value <SortIcon dir={sortKey === 'estimated_value' ? sortDir : null} /></th>
                  <th {...getProps('total_project_cost')}>Client Project Value <SortIcon dir={sortKey === 'total_project_cost' ? sortDir : null} /></th>
                  <th {...getProps('stage')}>Stage <SortIcon dir={sortKey === 'stage' ? sortDir : null} /></th>
                  <th {...getProps('expected_close_date')}>Expected Close <SortIcon dir={sortKey === 'expected_close_date' ? sortDir : null} /></th>
                  <th {...getProps('owner')}>Owner <SortIcon dir={sortKey === 'owner' ? sortDir : null} /></th>
                  <th {...getProps('created_at')}>Created <SortIcon dir={sortKey === 'created_at' ? sortDir : null} /></th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagination.paged.map((opp) => (
                  <tr
                    key={opp.id}
                    className={`cursor-pointer hover:bg-slate-50 transition-colors ${selection.isSelected(opp.id) ? 'bg-teal-50/40' : ''}`}
                    onClick={() => openEditForm(opp)}
                    data-testid={`opp-row-${opp.id}`}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="w-4 h-4 accent-[#1F7A6E] cursor-pointer" checked={selection.isSelected(opp.id)} onChange={() => selection.toggle(opp.id)} data-testid={`select-opp-${opp.id}`} />
                    </td>
                    <td className="font-display font-semibold text-[#0F172A]">
                      <div className="flex items-center gap-2">
                        {opp.project_name}
                        <StaleBadge record={opp} variant="pill" />
                      </div>
                    </td>
                    <td className="text-xs">{opp.client}</td>
                    <td className="font-display font-bold text-xs">{opp.estimated_value ? inrCompact(opp.estimated_value) : '—'}</td>
                    <td className="font-display font-bold text-xs">{opp.total_project_cost ? inrCompact(opp.total_project_cost) : '—'}</td>
                    <td><span className={`tag ${stageColor(opp.stage)}`}>{opp.stage}</span></td>
                    <td className="text-xs">{opp.expected_close_date ? new Date(opp.expected_close_date).toLocaleDateString() : '-'}</td>
                    <td className="text-xs">{opp.owner}</td>
                    <td className="text-xs whitespace-nowrap">{formatDate(opp.created_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2 items-center">
                        <button onClick={(e) => handleDelete(opp.id, e)} className="text-red-600 hover:text-red-700" title="Remove project" data-testid={`del-opp-${opp.id}`}><Trash2 size={14} /></button>
                        <ChevronRight size={14} className="text-slate-400" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {opportunities.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">No projects in pipeline yet. Click "Add Project" to get started.</div>
            )}
            {opportunities.length > 0 && (
              <div className="border-t border-slate-200 px-4 py-3">
                <Pagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  start={pagination.start}
                  end={pagination.end}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                  label="projects"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Add / Edit form modal ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">
                {editingOpp ? 'Edit Project' : 'Add to Projects Pipeline'}
              </h2>
              <button onClick={resetForm} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Company *</label>
                  <CompanyPicker
                    value={formData.company_id}
                    onChange={(id, obj) => setFormData({ ...formData, company_id: id, client: obj?.name || formData.client, primary_contact_id: '' })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Primary Contact</label>
                  <ContactPicker
                    value={formData.primary_contact_id}
                    companyId={formData.company_id || null}
                    refreshSignal={contactRefresh}
                    onChange={(id, obj) => setFormData({ ...formData, primary_contact_id: id, client_email: obj?.email || formData.client_email })}
                    onAddNew={() => { setDrawerTarget('primary'); setContactDrawerOpen(true); }}
                  />
                  {!formData.company_id && (
                    <p className="text-[10px] text-amber-700 mt-1">Pick a Company first so contacts can be filtered to that company.</p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="label">Additional Contacts</label>
                  <MultiContactPicker
                    value={formData.additional_contact_ids || []}
                    companyId={formData.company_id || null}
                    refreshSignal={contactRefresh}
                    excludeIds={formData.primary_contact_id ? [formData.primary_contact_id] : []}
                    onChange={(ids) => setFormData({ ...formData, additional_contact_ids: ids })}
                    onAddNew={() => { setDrawerTarget('additional'); setContactDrawerOpen(true); }}
                    testid="project-additional-contacts"
                  />
                </div>
                <div className="col-span-2">
                  <ConsultantPanel
                    value={formData.consultants || []}
                    onChange={(list) => setFormData({ ...formData, consultants: list })}
                  />
                </div>
                <div>
                  <label className="label">Project Name *</label>
                  <ProjectCombobox value={formData.project_name} onChange={(v) => setFormData({ ...formData, project_name: v })} testid="opp-project-name" required />
                </div>
                <div>
                  <label className="label">Client *</label>
                  <input type="text" value={formData.client} onChange={(e) => setFormData({ ...formData, client: e.target.value })} className="input" required placeholder="Auto-filled from company" />
                </div>
                <div className="col-span-2">
                  <label className="label">Location</label>
                  <LocationCombobox value={formData.location} onChange={(v) => setFormData({ ...formData, location: v })} testid="opp-location" />
                </div>
                <div>
                  <label className="label">Estimated Order Value (₹ Cr) <span className="text-[10px] text-slate-400">(optional)</span></label>
                  <input type="number" min="0" step="0.01" value={formData.estimated_value} onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })} className="input" placeholder="e.g. 1.2" data-testid="opp-form-construction-value" />
                </div>
                <div>
                  <label className="label">Client's Overall Project Value (₹ Cr) <span className="text-[10px] text-slate-400">(optional)</span></label>
                  <input type="number" min="0" step="0.01" value={formData.total_project_cost} onChange={(e) => setFormData({ ...formData, total_project_cost: e.target.value })} className="input" placeholder="e.g. 95" data-testid="opp-form-total-cost" />
                </div>
                <div>
                  <label className="label">Stage *</label>
                  <select value={formData.stage} onChange={(e) => setFormData({ ...formData, stage: e.target.value })} className="input" required data-testid="opp-form-stage">
                    <option value="" disabled>Select pipeline stage…</option>
                    {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Expected Close Date</label>
                  <input type="date" value={formData.expected_close_date} onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })} className="input" />
                </div>
                <div className="col-span-2">
                  <label className="label">Project Owner *</label>
                  <EmployeePicker value={formData.owner} onChange={(v) => setFormData({ ...formData, owner: v })} testid="opportunity-owner-picker" required restrictToTeam />
                </div>
              </div>

              {/* Source Links */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label !mb-0">Source Links <span className="text-[10px] text-slate-400">(optional, up to 3)</span></label>
                </div>
                <div className="space-y-2">
                  {(formData.source_links?.length ? formData.source_links : ['', '', '']).slice(0, 3).map((link, idx) => (
                    <input
                      key={idx}
                      type="url"
                      value={link || ''}
                      onChange={(e) => {
                        const next = [...(formData.source_links || ['', '', ''])];
                        while (next.length < 3) next.push('');
                        next[idx] = e.target.value;
                        setFormData({ ...formData, source_links: next });
                      }}
                      className="input"
                      placeholder={`https://… (link ${idx + 1})`}
                      data-testid={`opp-form-source-link-${idx}`}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input" rows={3} />
              </div>

              {/* Enquiry Documents */}
              <div className="pt-2" data-testid="opp-form-docs-section">
                <div className="flex items-center justify-between mb-2">
                  <label className="label !mb-0">Enquiry Received Documents</label>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Attach the site layout, concrete requirement schedule, structural drawings and any other enquiry papers the BD team received.
                </p>
                <DocumentUploader
                  entityType="opportunity"
                  entityId={savedOppIdRef.current}
                  documents={formDocs}
                  onChange={setFormDocs}
                  categories={ENQUIRY_DOC_CATEGORIES}
                  defaultCategory="Requirement Sheet"
                  resolveEntityId={ensureOppSaved}
                  testidPrefix="opp"
                  title="Add Enquiry Documents"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="opp-form-submit">
                  {editingOpp || savedOppIdRef.current ? 'Update' : 'Create'} Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── LeadDetailDrawer ── */}
      {openOppId && (
        <LeadDetailDrawer
          entityType="opportunity"
          entityId={openOppId}
          onClose={() => { setOpenOppId(null); load(); }}
          onUpdated={load}
          onEdit={(record) => {
            setOpenOppId(null);
            openEditForm(record);
          }}
        />
      )}

      {/* ── ContactFormDrawer ── */}
      <ContactFormDrawer
        open={contactDrawerOpen}
        onClose={() => setContactDrawerOpen(false)}
        prefill={{
          company_id: formData.company_id || '',
          company_name: formData.client || '',
        }}
        lockCompany={!!formData.company_id}
        onCreated={(newContact) => {
          setContactRefresh((n) => n + 1);
          if (drawerTarget === 'primary') {
            setFormData((fd) => ({
              ...fd,
              primary_contact_id: newContact.id,
              client_email: newContact.email || fd.client_email,
            }));
          } else {
            setFormData((fd) => ({
              ...fd,
              additional_contact_ids: [
                ...(fd.additional_contact_ids || []).filter((cid) => cid !== fd.primary_contact_id),
                newContact.id,
              ],
            }));
          }
        }}
      />
    </div>
  );
}
